import {objectFromEntries} from '@benchling/js-utils';

import {isColumnTypeCompatible} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import {DataFrameColumnMappingError} from 'analysis-tool/data-frame-creation/column-mapping/DataFrameColumnMappingTable';
import {
  DataFrameColumnToColumnMapping,
  DataMappingState,
} from 'analysis-tool/structured-data-mapping/DataMappingTypes';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export interface DataMappingValidationError {
  outputFieldId: string;
  message: string;
}

export interface ColumnMappingConfig {
  isRequired: boolean;
}

const DEFAULT_MAPPING_CONFIG: ColumnMappingConfig = {
  isRequired: true,
};

export default function validateSchemaMapping(
  dataMappingState: DataMappingState
): Array<DataMappingValidationError> {
  const fields = dataMappingState.resultSchema?.fields;
  if (!fields) {
    return [];
  }

  const errors: Array<DataMappingValidationError> = [];
  const usedColumnSystemNames = new Set<string>();

  for (const field of fields) {
    const mapping = dataMappingState.mappings.find((mapping) => mapping.outputFieldId === field.apiId);

    if (!mapping && field.isRequired) {
      errors.push({message: `${field.displayName} is a required field.`, outputFieldId: field.apiId});
    }

    if (mapping && field.fieldType === FieldType.JSON) {
      errors.push({message: `Mapping to JSON fields is not supported.`, outputFieldId: field.apiId});
    }

    if (mapping && usedColumnSystemNames.has(mapping.inputColumnSystemName)) {
      errors.push({
        message: `This column is mapped to a previous field.`,
        outputFieldId: field.apiId,
      });
    }

    if (mapping) {
      usedColumnSystemNames.add(mapping.inputColumnSystemName);
    }
  }

  return errors;
}

/**
 * Validates the column mappings between source and target .
 *
 * @param columnMappings - An array of mappings between input and output columns, using their system names.
 * @param outputColumnDefs - An array of output column definitions.
 * @param inputColumnDefs - An array of input column definitions.
 * @param config - Optional configuration containing
 *   outputColumnConfig, which specifies for a given output column, is a mapping required for it
 * @returns An array of errors found during validation.
 *
 * Future development: add inputColumnConfig, pairingConfig, so it can be used in replace table and join/union table components
 *
 * The function performs the following validations:
 * - Ensures each output column is mapped exactly once (error).
 * - Checks each input column is mapped at most once (warning).
 * - Ensures required target columns are mapped (error).
 * - Ensures the compatibility of column types between source and target columns (error).
 */
export function validateColumnMapping(
  columnMappings: ReadonlyArray<DataFrameColumnToColumnMapping>,
  outputColumnDefs: ReadonlyArray<DataFrameColumnDef>,
  inputColumnDefs: ReadonlyArray<DataFrameColumnDef>,
  config?: {
    outputColumnConfig?: Readonly<Record<DataFrameColumnDef['systemName'], ColumnMappingConfig>>;
  }
): Array<DataFrameColumnMappingError> {
  if (config == null) {
    config = {};
  }

  if (config.outputColumnConfig == null) {
    config.outputColumnConfig = {};
  }

  const errors: Array<DataFrameColumnMappingError> = [];

  // target column MUST be mapped no more than once
  const outputColumnSystemNameToColumnDefMap = objectFromEntries(
    outputColumnDefs.map((colDef) => [colDef.systemName, colDef])
  );
  const mappedTargetColumnSet = new Set();
  for (const {outputColumnSystemName} of columnMappings) {
    if (mappedTargetColumnSet.has(outputColumnSystemName)) {
      const displayName = outputColumnSystemNameToColumnDefMap[outputColumnSystemName].displayName;
      errors.push({
        systemName: outputColumnSystemName,
        title: 'Column mapped multiple times',
        description: `Duplicated mapping found for target column "${displayName}".`,
        level: 'ERROR',
      });
    }
    mappedTargetColumnSet.add(outputColumnSystemName);
  }

  // data frame column SHOULD be mapped once
  const mappedDataFrameColumnSet = new Set();
  for (const {inputColumnSystemName, outputColumnSystemName} of columnMappings) {
    if (mappedDataFrameColumnSet.has(inputColumnSystemName)) {
      errors.push({
        systemName: outputColumnSystemName,
        title: 'Column mapped already',
        description: `The column is mapped to another column already.`,
        level: 'WARNING',
      });
    }
    mappedDataFrameColumnSet.add(inputColumnSystemName);
  }

  const outputToInputColumnSystemNameMap = objectFromEntries(
    columnMappings.map(({inputColumnSystemName, outputColumnSystemName}) => [
      outputColumnSystemName,
      inputColumnSystemName,
    ])
  );
  const inputColumnSystemNameToColumnDefMap = objectFromEntries(
    inputColumnDefs.map((colDef) => [colDef.systemName, colDef])
  );
  for (const outputColumnDef of outputColumnDefs) {
    // output column may be configured to require a mapping
    const mappingConfig = config?.outputColumnConfig[outputColumnDef.systemName] ?? DEFAULT_MAPPING_CONFIG;
    if (!mappingConfig.isRequired) {
      continue;
    }
    if (!outputToInputColumnSystemNameMap.hasOwnProperty(outputColumnDef.systemName)) {
      errors.push({
        systemName: outputColumnDef.systemName,
        title: 'Column mapping required',
        description: `Mapping for column "${outputColumnDef.displayName}" is required.`,
        level: 'ERROR',
      });
      continue;
    }

    // column type MUST be compatible
    const inputColumnDef =
      inputColumnSystemNameToColumnDefMap[outputToInputColumnSystemNameMap[outputColumnDef.systemName]];
    if (!isColumnTypeCompatible(inputColumnDef, outputColumnDef)) {
      const isNullableMismatch = isColumnTypeCompatible(inputColumnDef, outputColumnDef, {
        alwaysAllowNullable: true,
      });
      errors.push({
        systemName: outputColumnDef.systemName,
        title: 'Column type mismatch',
        description: isNullableMismatch
          ? `Column type mismatch: input column allows empty cells, but output column does not.`
          : `Column type mismatch: "${inputColumnDef.type.leafType}" (input) to "${outputColumnDef.type.leafType}" (output).`,
        level: 'ERROR',
      });
    }
  }

  return errors;
}
